//#region ENUM AccessCodeBaseEnum
/**
 * Enum representing the various access permissions available in the system.
 * Each access code corresponds to a specific action or permission that a user can have.
 */
export enum AccessCodeBaseEnum {
  /**
   * Staff management - Can edit staff access permissions
   * @property {number} EditStaffAccess
   */
  EditStaffAccess = 0,

  /**
   * Stock viewing - Basic access to view stock information
   * @property {number} StockView
   */
  StockView = 1,

  /**
   * Stock editing - Can make changes to stock data
   * @property {number} StockEditAccess
   */
  StockEditAccess = 2,

  /**
   * Stock submission - Can submit stock changes to server
   * @property {number} StockEditSubmit
   */
  StockEditSubmit = 3,

  /**
   * NGP viewing - Can view Nominal Gross Profit data
   * @property {number} NgpView
   */
  NgpView = 4,

  /**
   * Auto-ordering - Basic access to auto-ordering tools
   * @property {number} AoToolAccess
   */
  AoToolAccess = 5,

  /**
   * Auto-ordering - Can view other users' orders
   * @deprecated Legacy — not assignable via Customise Permissions UI
   * @property {number} AoSeePeers
   */
  AoSeePeers = 6,

  /**
   * Auto-ordering - Can edit other users' orders
   * @deprecated Legacy — not assignable via Customise Permissions UI
   * @property {number} AoEditPeers
   */
  AoEditPeers = 7,

  /**
   * Auto-ordering - Can submit orders to suppliers
   * @property {number} AoSubmit
   */
  AoSubmit = 8,

  /**
   * Auto-ordering - Can edit delivery schedules
   * @property {number} AoEditSchedule
   */
  AoEditSchedule = 9,

  /**
   * Auto-ordering - Can handle unscheduled orders
   * @property {number} AoUnscheduled
   */
  AoUnscheduled = 10,

  /**
   * Auto-ordering - Can view/edit system settings
   * @property {number} AoViewEditSettings
   */
  AoViewEditSettings = 11,

  /**
   * Dashboard - Can view store dashboard data
   * @property {number} DashboardView
   */
  DashboardView = 12,

  /**
   * Shelf talkers - Can view shelf talker information
   * @property {number} ShelfTalkerView
   */
  ShelfTalkerView = 13,

  /**
   * Barcode scanning - Can view barcode feature
   * @property {number} BarcodeScannerHome
   */
  BarcodeScannerHome = 14,

  /**
   * Dashboard - Can access reporting features
   * @property {number} DashboardReporting
   */
  DashboardReporting = 15,

  /**
   * Stock take - Can count / edit stock takes (not process or delete)
   * @property {number} StockTakeCountAccess
   */
  StockTakeCountAccess = 16,

  /**
   * Stock take - Can process stock takes on server, delete stock takes, edit schedule
   * @property {number} StockTakeManagerAccess
   */
  StockTakeManagerAccess = 17,
}

//#endregion

//#region TYPE AccessCodeBaseType
/**
 * Type alias for `AccessCodeBaseEnum`.
 * Represents the access permissions available in the system.
 */
export type AccessCodeBaseType = AccessCodeBaseEnum;
//#endregion

//#region ENUM AccessCodeBitEnum
/**
 * Enum representing bitwise access codes for permissions.
 * Each permission is a power-of-two flag derived from the corresponding base enum index.
 *
 * This allows you to combine multiple access permissions using bitwise operations.
 *
 * @enum {number}
 */
export enum AccessCodeBitEnum {
  /**
   * No access granted.
   * @type {number}
   */
  NoAccess = 0 << AccessCodeBaseEnum.EditStaffAccess, // 0

  /**
   * Staff management - Can edit staff access permissions
   * @type {number}
   */
  EditStaffAccess = 1 << AccessCodeBaseEnum.EditStaffAccess, // 1

  /**
   * Stock viewing - Basic access to view stock information
   * @type {number}
   */
  StockView = 1 << AccessCodeBaseEnum.StockView, // 2

  /**
   * Stock editing - Can make changes to stock data
   * @type {number}
   */
  StockEditAccess = 1 << AccessCodeBaseEnum.StockEditAccess, // 4

  /**
   * Stock submission - Can submit stock changes to server
   * @type {number}
   */
  StockEditSubmit = 1 << AccessCodeBaseEnum.StockEditSubmit, // 8

  /**
   * NGP viewing - Can view Nominal Gross Profit data
   * @type {number}
   */
  NgpView = 1 << AccessCodeBaseEnum.NgpView, // 16

  /**
   * Auto-ordering - Basic access to auto-ordering tools
   * @type {number}
   */
  AoToolAccess = 1 << AccessCodeBaseEnum.AoToolAccess, // 32

  /**
   * Auto-ordering - Can view other users' orders
   * @deprecated Legacy — not assignable via Customise Permissions UI
   * @type {number}
   */
  AoSeePeers = 1 << AccessCodeBaseEnum.AoSeePeers, // 64

  /**
   * Auto-ordering - Can edit other users' orders
   * @deprecated Legacy — not assignable via Customise Permissions UI
   * @type {number}
   */
  AoEditPeers = 1 << AccessCodeBaseEnum.AoEditPeers, // 128

  /**
   * Auto-ordering - Can submit orders to suppliers
   * @type {number}
   */
  AoSubmit = 1 << AccessCodeBaseEnum.AoSubmit, // 256

  /**
   * Auto-ordering - Can edit delivery schedules
   * @type {number}
   */
  AoEditSchedule = 1 << AccessCodeBaseEnum.AoEditSchedule, // 512

  /**
   * Auto-ordering - Can handle unscheduled orders
   * @type {number}
   */
  AoUnscheduled = 1 << AccessCodeBaseEnum.AoUnscheduled, // 1024

  /**
   * Auto-ordering - Can view/edit system settings
   * @type {number}
   */
  AoViewEditSettings = 1 << AccessCodeBaseEnum.AoViewEditSettings, // 2048

  /**
   * Dashboard - Can view store dashboard data
   * @type {number}
   */
  DashboardView = 1 << AccessCodeBaseEnum.DashboardView, // 4096

  /**
   * Shelf talkers - Can view shelf talker information
   * @type {number}
   */
  ShelfTalkerView = 1 << AccessCodeBaseEnum.ShelfTalkerView, // 8192

  /**
   * Barcode scanning - Can view barcode feature
   * @type {number}
   */
  BarcodeScannerHome = 1 << AccessCodeBaseEnum.BarcodeScannerHome, // 16384

  /**
   * Dashboard - Can access reporting features
   * @type {number}
   */
  DashboardReporting = 1 << AccessCodeBaseEnum.DashboardReporting, // 32768

  /**
   * Stock take - Can count / edit stock takes (not process or delete)
   * @type {number}
   */
  StockTakeCountAccess = 1 << AccessCodeBaseEnum.StockTakeCountAccess, // 65536

  /**
   * Stock take - Can process stock takes on server, delete stock takes, edit schedule
   * @type {number}
   */
  StockTakeManagerAccess = 1 << AccessCodeBaseEnum.StockTakeManagerAccess, // 131072

  /**
   * Full administrative access across all features and stores.
   * Combination of all access bits.
   * @type {number}
   */

  Admin =
    EditStaffAccess |
    StockView |
    StockEditAccess |
    StockEditSubmit |
    NgpView |
    AoToolAccess |
    AoSeePeers |
    AoEditPeers |
    AoSubmit |
    AoEditSchedule |
    AoUnscheduled |
    AoViewEditSettings |
    DashboardView |
    ShelfTalkerView |
    BarcodeScannerHome |
    DashboardReporting |
    StockTakeCountAccess |
    StockTakeManagerAccess, // 262143 = 0x3FFFF
}

//#endregion

//#region TYPE AccessCodeBitType
/**
 * Type alias for `AccessCodeBitEnum`.
 * Represents the bitwise access codes used for combining multiple permissions.
 */
export type AccessCodeBitType = AccessCodeBitEnum;
//#endregion


export const USER_ROLL_DESCRIPTION: Record<string, string> = {
  ["Admin"]: "Full access to all system features, settings, and staff management.",
  ["Barcode Scanner"]: "Under Construction.",
  ["Manager"]: "Full feature access including stock, ordering, and settings, but excludes staff access management.",
  ["Stock Manager"]: "Full stock operations, old-models, NGP, shelf talkers, and AO settings; no dashboard or staff access.",
  ["Stock Editor"]: "Can view/edit stock, view NGP, and manage shelf talkers; no old-models or dashboard access.",
  ["Ordering Only"]: "Manages all old-models tasks and settings, and can view/edit stock information.",
  ["Stock Viewer"]: "Can view and edit stock data; no submission, ordering, or other feature access.",
  ["Shelf Talkers Only"]: "Manages shelf talkers and views stock information; no other feature access.",
};
